import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { FiPlus, FiUser, FiLoader } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { usuariosAPI } from '../services/api';
import theme from '../styles/theme';
import Layout from '../components/Layout';
import { UsuarioCard, UsuariosFiltros, UsuariosPaginacao } from '../components/usuarios';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[6]};
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  margin-bottom: ${theme.spacing[4]};
`;

const AddButton = styled.button`
  padding: ${theme.spacing[3]} ${theme.spacing[6]};
  background: ${theme.colors.primary[500]};
  color: ${theme.colors.text.primary};
  border: none;
  border-radius: ${theme.borderRadius.xl};
  font-size: ${theme.fontSizes.base};
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};

  &:hover {
    background: ${theme.colors.primary[600]};
    transform: translateY(-2px);
    box-shadow: 0 4px 12px ${theme.colors.primary[500]}40;
  }

  &:active {
    transform: translateY(0);
  }
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[6]};
`;

const CardsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: ${theme.spacing[6]};
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: ${theme.spacing[12]} ${theme.spacing[6]};
  color: ${theme.colors.text.muted};
`;

const LoadingSpinner = styled.div`
  font-size: ${theme.fontSizes['2xl']};
  animation: spin 1s linear infinite;
  margin-bottom: ${theme.spacing[4]};

  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

const LoadingText = styled.p`
  font-size: ${theme.fontSizes.base};
  margin: 0;
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: ${theme.spacing[12]} ${theme.spacing[6]};
  text-align: center;
`;

const EmptyIcon = styled.div`
  font-size: ${theme.fontSizes['4xl']};
  color: ${theme.colors.text.muted};
  margin-bottom: ${theme.spacing[4]};
`;

const EmptyTitle = styled.h3`
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSizes.xl};
  font-weight: 600;
  margin: 0 0 ${theme.spacing[2]} 0;
`;

const EmptyText = styled.p`
  color: ${theme.colors.text.muted};
  font-size: ${theme.fontSizes.base};
  margin: 0 0 ${theme.spacing[6]} 0;
`;

const EmptyAction = styled.button`
  padding: ${theme.spacing[3]} ${theme.spacing[6]};
  background: ${theme.colors.primary[500]};
  color: ${theme.colors.text.primary};
  border: none;
  border-radius: ${theme.borderRadius.xl};
  font-size: ${theme.fontSizes.base};
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};

  &:hover {
    background: ${theme.colors.primary[600]};
    transform: translateY(-2px);
  }
`;

const ErrorState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: ${theme.spacing[12]} ${theme.spacing[6]};
  text-align: center;
`;

const ErrorIcon = styled.div`
  font-size: ${theme.fontSizes['4xl']};
  color: ${theme.colors.error[500]};
  margin-bottom: ${theme.spacing[4]};
`;

const ErrorTitle = styled.h3`
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSizes.xl};
  font-weight: 600;
  margin: 0 0 ${theme.spacing[2]} 0;
`;

const ErrorText = styled.p`
  color: ${theme.colors.text.muted};
  font-size: ${theme.fontSizes.base};
  margin: 0 0 ${theme.spacing[6]} 0;
`;

const ErrorAction = styled.button`
  padding: ${theme.spacing[3]} ${theme.spacing[6]};
  background: ${theme.colors.error[500]};
  color: ${theme.colors.text.primary};
  border: none;
  border-radius: ${theme.borderRadius.xl};
  font-size: ${theme.fontSizes.base};
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${theme.colors.error[600]};
  }
`;

const Usuarios = () => {
  const navigate = useNavigate();
  const [usuarios, setUsuarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [permissoes, setPermissoes] = useState([]);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 10,
    hasNextPage: false,
    hasPrevPage: false
  });

  const [filtros, setFiltros] = useState({
    search: '',
    ativo: '',
    telefone: '',
    permissao: '',
    page: 1,
    limit: 10
  });

  // Carregar usuários
  const carregarUsuarios = async (params) => {
    try {
      setLoading(true);
      setError(null);

      const response = await usuariosAPI.listar(params);
      
      if (response.data.success) {
        setUsuarios(response.data.data);
        setPagination(response.data.pagination);
      } else {
        setError('Erro ao carregar usuários');
      }
    } catch (err) {
      console.error('Erro ao carregar usuários:', err);
      setError('Erro ao conectar com o servidor');
    } finally {
      setLoading(false);
    }
  };

  // Carregar usuários na inicialização
  useEffect(() => {
    carregarUsuarios(filtros);
  }, []);

  useEffect(() => {
    const carregarPermissoes = async () => {
      try {
        const response = await usuariosAPI.listarPermissoes();
        if (response.data.success) {
          setPermissoes(response.data.data);
        }
      } catch (err) {
        console.error('Erro ao carregar permissões:', err);
      }
    };

    carregarPermissoes();
  }, []);

  // Atualizar filtros
  const handleFiltrosChange = (novosFiltros) => {
    setFiltros({
      ...novosFiltros,
      page: 1, // Reset para primeira página quando mudar filtros
      limit: filtros.limit || 10 // Manter o limite atual
    });
  };

  // Buscar com filtros (aceita parâmetros opcionais para usar filtros específicos)
  const handleBuscar = (param = null) => {
    if (param && typeof param === 'object' && typeof param.preventDefault === 'function') {
      param.preventDefault();
      carregarUsuarios({ ...filtros, page: 1 });
      return;
    }

    const filtrosParaBusca = param || filtros;
    carregarUsuarios(filtrosParaBusca);
  };

  // Mudar página
  const handlePageChange = (page) => {
    const novosFiltros = { ...filtros, page };
    setFiltros(novosFiltros);
    carregarUsuarios(novosFiltros);
  };

  // Mudar registros por página
  const handleItemsPerPageChange = (newLimit) => {
    const novosFiltros = { 
      ...filtros, 
      limit: newLimit,
      page: 1 // Reset para primeira página quando mudar o limite
    };
    setFiltros(novosFiltros);
    carregarUsuarios(novosFiltros);
  };

  // Navegar para adicionar usuário
  const handleAdicionarUsuario = () => {
    navigate('/usuarios/novo');
  };

  // Renderizar conteúdo baseado no estado
  const renderContent = () => {
    if (loading) {
      return (
        <LoadingContainer>
          <LoadingSpinner>
            <FiLoader />
          </LoadingSpinner>
          <LoadingText>Carregando usuários...</LoadingText>
        </LoadingContainer>
      );
    }

    if (error) {
      return (
        <ErrorState>
          <ErrorIcon>⚠️</ErrorIcon>
          <ErrorTitle>Erro ao carregar usuários</ErrorTitle>
          <ErrorText>{error}</ErrorText>
          <ErrorAction onClick={() => carregarUsuarios()}>
            Tentar novamente
          </ErrorAction>
        </ErrorState>
      );
    }

    if (usuarios.length === 0) {
      return (
        <EmptyState>
          <EmptyIcon>
            <FiUser />
          </EmptyIcon>
          <EmptyTitle>Nenhum usuário encontrado</EmptyTitle>
          <EmptyText>
            Não foram encontrados usuários com os filtros aplicados.
          </EmptyText>
          <EmptyAction onClick={handleAdicionarUsuario}>
            <FiPlus />
            Adicionar Primeiro Usuário
          </EmptyAction>
        </EmptyState>
      );
    }

    return (
      <>
        <CardsGrid>
          {usuarios.map((usuario) => (
            <UsuarioCard 
              key={usuario.uuid || usuario.id} 
              usuario={usuario}
            />
          ))}
        </CardsGrid>

        {pagination.totalPages > 1 && (
          <UsuariosPaginacao
            pagination={pagination}
            onPageChange={handlePageChange}
            onItemsPerPageChange={handleItemsPerPageChange}
            loading={loading}
          />
        )}
      </>
    );
  };

  return (
    <Layout title="Usuários">
      <Container>
        <Header>
          <AddButton onClick={handleAdicionarUsuario}>
            <FiPlus />
            Adicionar Usuário
          </AddButton>
        </Header>

        <Content>
          <UsuariosFiltros
            filtros={filtros}
            permissoes={permissoes}
            onFiltrosChange={handleFiltrosChange}
            onBuscar={handleBuscar}
            loading={loading}
          />

          {renderContent()}
        </Content>
      </Container>
    </Layout>
  );
};

export default Usuarios;
